import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Globals } from '../shared/globals';
import { Injectable } from '@angular/core';
import { StationPlugType } from '../models/station-plug-type.model';
import { StationAccessType } from '../models/station-access-type.model';
import { StationType } from '../models/station-type.model';
import { OcppLog } from '../models/ocpp-log.model';

@Injectable()
export class MaintenanceService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private globals: Globals
  ) {
    this.baseUrl = globals.apiUrl('Maintenance');
  }

  fetchStations(): Promise<MaintenanceStation[]> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(this.baseUrl, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  deleteStation(id: string): Promise<boolean> {
    const url = this.globals.apiUrl('StationsOwner') + '?id=' + id;
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .delete(url, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            // 1 -> ok
            // 3 -> unknonw err
            return response === 1;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  patchStation(delta: MaintenanceStationPatch): Promise<boolean> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .patch(this.baseUrl, delta, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  unlockConnector(stationId: string, connectorId: number): Promise<boolean> {
    const url =
      this.baseUrl +
      '/' +
      stationId +
      '/unlock?connectorId=' +
      connectorId.toString();
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .post(url, null, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  resetConnector(stationId: string, isSoftReset: boolean): Promise<any> {
    const url = this.baseUrl + '/' + stationId + '/reset?soft=' + isSoftReset;
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .post(url, '', {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  fetchDiagnostics(
    stationId: string,
    ftpLocation: string,
    from: Date,
    to: Date
  ): Promise<any> {
    const fromDate = (from.getTime() / 1000).toString();
    const toDate = (to.getTime() / 1000 + 1).toString();
    const url = `${this.baseUrl}/${stationId}/diagnostics?ftpLocation=${ftpLocation}&from=${fromDate}&to=${toDate}`;

    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(url, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  changeConfig(
    stationId: string,
    keyValue: ConfigurationKeyValue
  ): Promise<ConfigurationKeyValue> {
    const url = this.baseUrl + '/' + stationId + '/configuration';
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .post(url, keyValue, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  getConfig(stationId: string, key: string): Promise<ConfigurationKeyValue> {
    const url = this.baseUrl + '/' + stationId + '/configuration?key=' + key;
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(url, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  uploadFirmware(stationId: string, ftpLocation: string): Promise<any> {
    const url =
      this.baseUrl + '/' + stationId + '/firmware?ftpLocation=' + ftpLocation;
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .post(url, null, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }

  getLogs(
    stationId: string,
    from: Date,
    to: Date,
    offset: Number
  ): Promise<OcppLog[]> {
    // Adding one second to the end time fixes a bizarre bug where there would be no results
    // at all when the cloud queries the database. This is a workaround for a possible
    // bug in the Postgres library the cloud uses to query the database.
    to.setUTCDate(to.getUTCDate() + 1);
    from.setUTCDate(from.getUTCDate() + 1);
    const fromDate = (from.getTime() / 1000).toString();
    const toDate = (to.getTime() / 1000 + 1).toString();

    const url = this.globals.apiUrl(
      `stations/${stationId}/ocpp/logs?from=${fromDate}&to=${toDate}&offset=${offset}`
    );
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(url, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch(() => {
        Promise.reject();
      });
  }
}

export class MaintenanceStationPatch {
  StationId: string;
  SubType: StationSubType;
}

export class ConfigurationKeyValue {
  Key: string;
  Value: string;
}

export class MaintenanceStation {
  Id: string;
  Name: string;
  Address: string;
  Type: StationType;
  SubType: StationSubType;
  PlugType: StationPlugType;
  AccessType: StationAccessType;
  Connectors: MaintenanceConnector[];
}

export class MaintenanceConnector {
  Position: number;
  Name: string;
}

export enum StationSubType {
  OnOffStation = 0,
  WallBeStationV1 = 1,
  JuiceBoosterV1 = 2,
  EnercabWallboxT2 = 3,
  LadEV1 = 4,
}
